import { useState } from 'react';

import {
  useGetArtifactDigestQuery,
  useGetArtifactFactsQuery,
  useGetArtifactReferencesQuery,
  useGetMentionedProvidersQuery,
} from '@/services/api/artifactDigestService';
import { useGetBlobWithMarkdownContentQuery } from '@/services/api/blobService';
import { ArtifactType } from '@/services/types/artifact-digest-types';
import { ArtifactUnpacked } from '@/services/types/artifact-types';

import { DigestOverview } from './DigestOverview';
import { FactsSection } from './sections/FactsSection';
import { OCRPreview } from './sections/MarkdownSection';
import { ProvidersSection } from './sections/ProvidersSection';
import { ReferencesSection } from './sections/ReferencesSection';

type TabKey = 'overview' | 'providers' | 'references' | 'facts' | 'markdown';

interface DigestDebugTabProps {
  unpackedArtifact: ArtifactUnpacked;
}

export const DigestDebugTab = ({ unpackedArtifact }: DigestDebugTabProps) => {
  const { id: artifactId, artifact_type: artifactType } = unpackedArtifact;
  const [activeTab, setActiveTab] = useState<TabKey>('overview');

  // Fetch all needed data via API
  const { data: digest } = useGetArtifactDigestQuery(artifactId, {
    skip: !artifactId,
  });

  const { data: facts = [] } = useGetArtifactFactsQuery(artifactId, {
    skip: !artifactId,
  });

  const { data: providers = [] } = useGetMentionedProvidersQuery(artifactId, {
    skip: !artifactId,
  });

  const { data: references = [] } = useGetArtifactReferencesQuery(artifactId, {
    skip: !artifactId,
  });

  const { data: markdownContent } = useGetBlobWithMarkdownContentQuery(
    {
      matterId: unpackedArtifact.matter_id,
      blobId: unpackedArtifact.blob?.id || '',
    },
    {
      skip: !artifactId || artifactType !== ArtifactType.BLOB,
    },
  );

  // Data availability flags
  const hasDigest = !!digest;
  const hasProviders = !!providers?.length;
  const hasReferences = !!references?.length;
  const hasFacts = !!facts?.length;
  const hasMarkdown = !!markdownContent?.markdown_content;

  const tabs = [
    { key: 'overview' as TabKey, label: 'Overview', enabled: hasDigest },
    {
      key: 'providers' as TabKey,
      label: 'Providers',
      enabled: hasProviders,
      count: providers?.length,
    },
    {
      key: 'references' as TabKey,
      label: 'References',
      enabled: hasReferences,
      count: references?.length,
    },
    {
      key: 'facts' as TabKey,
      label: 'Facts',
      enabled: hasFacts,
      count: facts?.length,
    },
    { key: 'markdown' as TabKey, label: 'Markdown', enabled: hasMarkdown },
  ];

  // If the active tab is not available, default to the first available tab
  if (!tabs.find((tab) => tab.key === activeTab)?.enabled) {
    const firstAvailableTab = tabs.find((tab) => tab.enabled);
    if (firstAvailableTab) {
      setActiveTab(firstAvailableTab.key);
    }
  }

  if (
    !hasDigest &&
    !hasProviders &&
    !hasReferences &&
    !hasFacts &&
    !hasMarkdown
  ) {
    return (
      <div className="p-6 text-center text-sm text-muted-foreground">
        This artifact has not been processed yet—hang tight or try regenerating
        / refreshing.
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Tab navigation */}
      <div className="border-b border-zinc-200 dark:border-zinc-800 flex flex-wrap gap-3 p-3">
        {tabs.map((tab) => (
          <button
            type="button"
            key={tab.key}
            className={`px-4 py-1.5 text-xs rounded-full transition-colors ${
              tab.enabled
                ? activeTab === tab.key
                  ? 'bg-primary/90 text-white'
                  : 'bg-muted text-foreground hover:bg-muted/60'
                : 'bg-muted/40 text-muted-foreground cursor-not-allowed'
            }`}
            onClick={() => tab.enabled && setActiveTab(tab.key)}
            disabled={!tab.enabled}
          >
            {tab.label}
            {tab.count !== undefined && (
              <span className="ml-1 px-1 py-0.5 text-[9px] bg-black/10 dark:bg-white/10 rounded-full">
                {tab.count}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Tab content */}
      <div className="p-4 h-full">
        {activeTab === 'overview' && (
          <DigestOverview digest={digest} artifactId={artifactId} />
        )}
        {activeTab === 'providers' && (
          <ProvidersSection providers={providers || []} />
        )}
        {activeTab === 'references' && (
          <ReferencesSection references={references || []} />
        )}
        {activeTab === 'facts' && <FactsSection facts={facts || []} />}
        {activeTab === 'markdown' && (
          <OCRPreview
            content={markdownContent?.markdown_content || null}
            blocks={markdownContent?.blocks || []}
          />
        )}
      </div>
    </div>
  );
};
